import React, { useRef, useState, useEffect } from "react";
import {
  Search,
  Minus,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  Trash2
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage,faChevronDown, faPlusCircle, faQuestionCircle, faTrash, faX , faExclamationTriangle, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { decryptData } from "../../../utils/storageCrypto";
import useCommonResponseHandler from '../../CommenResponseAndErrorHandlers/CommenResponseHandler';
import { Loader ,Spinner } from '../../Loader/Loader';

// Custom hook to handle outside clicks
const useOutsideClick = (ref, callback) => {
  const handleClick = (e) => {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);
};

const CustomDropdown = ({ options, selected, onSelect, showAddNew, placeholder, showSearch }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);
  const dropdownRef = React.useRef(null);
  const searchInputRef = useRef(null);
  

  const filteredOptions = options.filter(option => {
    if (!option || !option.label) return false;
    return option.label.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOptionClick = (option) => {
    onSelect(option); // Pass the entire option object
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) return; // Only handle keydown events when the dropdown is open
  
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? 0 : Math.min(prevIndex + 1, filteredOptions.length - 1);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedOptionIndex(prevIndex => {
          const newIndex = prevIndex === null ? filteredOptions.length - 1 : Math.max(prevIndex - 1, 0);
          scrollToOption(newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedOptionIndex !== null) {
          const selectedOption = filteredOptions[focusedOptionIndex];
          handleOptionClick(selectedOption);
        }
        break;
      default:
        break;
    }
  };
  const scrollToOption = (index) => {
    const optionElements = dropdownRef.current.querySelectorAll('div[data-index]');
    if (optionElements && optionElements[index]) {
      optionElements[index].scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  };

  useOutsideClick(dropdownRef, () => setIsOpen(false));
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className="flex justify-between items-center p-2 border bg-white border-gray-300 rounded-md cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
  role="button" // Add role prop
        onKeyDown={(e) => isOpen && handleKeyDown(e)} // Only call handleKeyDown when the dropdown is open
      >
        <span className={`text-sm ${selected ? 'text-black' : 'text-[#838195]'}`}>
          {selected?.label || placeholder} {/* Use selected.label if selected is an object */}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 text-gray-500" />
      </div>
      {isOpen && (
        <div className="absolute mt-1 w-full max-h-40 bg-white border border-gray-300 rounded-md shadow-md z-10 flex flex-col">
          {showSearch && (
            <input
              type="text"
              className="p-1 border rounded-md border-blue-500 focus:outline-none hover:border-blue-500 focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus={isOpen}
            />
          )}
          <div className="overflow-y-auto scrollbar-visible flex-grow">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  data-index={index}
                  className={`p-2 cursor-pointer text-sm ${index === focusedOptionIndex ? 'bg-blue-500 text-white' : ''} hover:bg-blue-500 hover:text-white`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.icon && <FontAwesomeIcon icon={option.icon} className="mr-2" />}
                  {option.label}
                </div>
              ))
            ) : (
              <div className="p-2 text-sm text-gray-500">
                Not available
              </div>
            )}
          </div>
          {showAddNew && (
            <div
              className="p-2 cursor-pointer text-sm text-blue-500 border-t border-gray-300"
              onClick={() => handleOptionClick({value: 'add-new', label: 'Add New'})}
            >
              {options.find(option => option.value === 'add-new')?.icon && (
                <FontAwesomeIcon
                  icon={options.find(option => option.value === 'add-new').icon}
                  className="mr-2"
                />
              )}
              {options.find(option => option.value === 'add-new')?.label}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const CreateRFQ = ({ isOpen, onClose }) => {
  const [pricingModel, setPricingModel] = useState("general");
  const [searchQuery, setSearchQuery] = useState("");
  const [items, setItems] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [allProducts, setAllProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState();
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const [deliveryAddresses, setDeliveryAddresses] = useState([]);
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  const fetchurl = process.env.REACT_APP_FORE_BRICKBUCKET;
  const imageurl = process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES;
  const FetchProjectsUrl = process.env.REACT_APP_FORE_APILINK; //measurit
  const RETRY_LIMIT = 3; // Maximum number of retries
 const { 
    showPopup, popupMessage, popupType,retryFunction, handleGetResponse, handleSaveResponse, setShowPopup,
    isSaving, setRetryFunction, handleDeleteResponse,  setIsSaving ,    setPopupMessage,
    setPopupType } = useCommonResponseHandler();


  useEffect(() => {
    if (isOpen) {
      fetchAllApprovedCustomers();
      fetchAllItems();
    }
  }, [isOpen]);

  useEffect(() => {
  if (selectedCustomer?.id) {
          fetchDeliveryAddresses(selectedCustomer.id);
  }
}, [selectedCustomer]);



  const fetchAllItems = async () => {
    try {
          const encryptedToken = sessionStorage.getItem('token');
              const token = decryptData(encryptedToken);
              const encryptedOrganizationId = sessionStorage.getItem('organizationId');
              const organizationId = decryptData(encryptedOrganizationId);

      if (!organizationId) {
        console.error("Organization ID not found in sessionStorage.");
        return;
      }

      const response = await fetch(
        `${fetchurl}product/get-all-products?organization_id=${organizationId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-token": token,
          },
        }
      );

      const data = await response.json();
      console.log("Fetched Products:", data);
      if (data.success && data.data) {
        setAllProducts(data.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

const handleQuantityChange = (id, value, index) => {
  setItems((prev) => {
    const newItems = [...prev];
    const item = newItems[index];

    // If quantity is 1 and action is 'dec', remove the item
    if (item.quantity === 1 && value === "dec") {
      return newItems.filter((_, i) => i !== index);
    }

    // Otherwise, update the quantity
    newItems[index] = {
      ...item,
      quantity:
        typeof value === "number"
          ? Math.max(1, value) // Direct input: ensure min 1
          : value === "inc"
          ? item.quantity + 1 // Increment
          : Math.max(1, item.quantity - 1), // Decrement, min 1
    };

    return newItems;
  });
};


  const handleAddItem = () => {
  const trimmedName = searchQuery.trim();
  if (!trimmedName) return;

  const existing = items.find(
    (item) => item.name.toLowerCase() === trimmedName.toLowerCase()
  );

  if (existing) {
    console.log("✅ Item already exists. Increasing quantity:", existing);
    handleQuantityChange(existing.id, "inc");
    setSearchQuery("");
    setShowSuggestions(false);
    return;
  }

  const foundProduct = allProducts.find(
    (p) => p.product_name.toLowerCase() === trimmedName.toLowerCase()
  );

  console.log("🔍 Found Product:", foundProduct);

  let newItem = {
  name: foundProduct ? foundProduct.product_name : trimmedName,
  image: foundProduct?.product_image ? `${imageurl}${foundProduct.product_image}` : null,
  quantity: 1,
  is_available: true,
  product_id:foundProduct._id,
  selling_price:foundProduct.selling_price,
  cost_price:foundProduct.cost_price,
  tax_rate:foundProduct.GST, 
  UOM_id:foundProduct.UOM_id._id
};

 if (foundProduct){
  newItem.product_id = foundProduct._id
  newItem.UOM_id = foundProduct.UOM_id
  newItem.product_category_id = foundProduct.product_category_id._id
 }else{
 newItem.is_available = false
 }

  console.log("🆕 Adding Item:", newItem);
  if (foundProduct?.product_image) {
    console.log("📸 Item has image:", newItem.image);
  } else {
    console.log("⚠️ Item has no image, using placeholder");
  }

  setItems((prev) => [...prev, newItem]);
  setSearchQuery("");
  setShowSuggestions(false);
};


const calculateTotals = (cartItems/* , redeemableItems */) => {

  let subtotal = 0;
  let taxAmount = 0;

  cartItems.forEach(cartItem => {

    // find the redeemable item by ID
    //const redeemItem = redeemableItems?.find(r => r.item_id === cartItem._id);

    // If redeem data not found → no coins
    //const coins = redeemItem ? redeemItem.coins : 0;

    // reduce selling price by coins
    //const effectivePrice = Math.max(cartItem.selling_price - coins, 0);

    // GST % (ex: 18)
    const gstRate = cartItem.GST || cartItem.IGST || 0;
    const eachSubtotal = cartItem.selling_price * cartItem.quantity;
    const effectivePrice = eachSubtotal /* - coins */;
    // Subtotal calculation (normal selling price × qty)
    subtotal += cartItem.selling_price * cartItem.quantity;

    // Correct tax calculation
    taxAmount += (effectivePrice * (gstRate / 100));
  });

  // Round the tax
  taxAmount = Math.round(taxAmount);

  return { subtotal, taxAmount };
};

 const createAndSendQuotation = async () => {
  try {
      console.log('🆕 Creating new quotation');
      // Get cart items from state (only for new quotations)
      if (!items.length) {
        return { success: false, message: 'Add items to create RFQ' };
      }

    console.log("items for rfq:",items);  

    const encryptedToken = sessionStorage.getItem('token');
    const token = decryptData(encryptedToken);
    const enorganizationId = sessionStorage.getItem('organizationId')
    const organizationId = decryptData(enorganizationId);

    const customerId = selectedCustomer?.id;

    if (!customerId || !token || !organizationId) {
      return { success: false, message: 'User information is incomplete or missing' };
    }

let quotationPayload;
const { subtotal, taxAmount } = calculateTotals(items/*,  redeemableItems */);
         
// Calculate total amount including tax and discount
const totalAmount = subtotal + taxAmount;
// Extract unique product category IDs from cart items
 const product_category_ids = [
  ...new Set(items.map(item => item.product_category_id))
];
// Build quotation payload
 quotationPayload = {
  quotation_date: new Date().toISOString(),
  customer_id:customerId ,
  project_id: selectedProject?.id || null,
  delivery_address_id: selectedDeliveryAddress?._id || null,
  tax_preference: "Taxable",
  organizationId: organizationId,
  status: "Pending",
  is_digistore: true,

  // Map cart items into quotation items
  items: items.map(item => {
    const totalPrice = item.selling_price * item.quantity;
    const taxRate = item.tax_rate / 100;
    const totalTaxAmount = totalPrice * taxRate;
    const totalItemAmount = totalPrice + totalTaxAmount;

    

    return {
      item_id: item.product_id,
      item_image: item.image,
      item_name: item.name,
      quantity: item.quantity,
      selling_price: item.selling_price,
      cost_price: item.cost_price,
      tax_rate: Math.round(taxRate),
      total_tax_amount: Math.round(+totalTaxAmount),
      total: Math.round(+totalItemAmount),
      UOM_id: item?.UOM_id,
    };
  }),


  product_category_ids: product_category_ids,
  subtotal: subtotal,
  tax_amount: taxAmount,
  discount: 0,
  total: totalAmount ,
  terms_and_conditions: "Standard terms and conditions apply",
};


    console.log('📤 Sending quotation data:', quotationPayload);

    const endpoint = `${fetchurl}customer/quotations/save-quickbuy-quotation`;
    const method = 'POST';

    // Send to backend
    const response = await fetch(endpoint, {
      method: method,
      headers: {
        'Content-Type': 'application/json',
        'x-token': token,
      },
      body: JSON.stringify(quotationPayload)
    });

    const data = await response.json();
    console.log('📊 Backend response:', data);

    if (data.success) {

      const finalQuotationId = data.data._id;
      console.log('💰 Final quotation ID:', finalQuotationId);

      return { 
        success: true, 
        quotationNumber: data.data.quotation_number 
      };

    } else {
      return { success: false, message: data.message || 'Failed to process quotation' };
    }

  } catch (error) {
    console.error('Error processing quotation:', error);
    return { success: false, message: 'An error occurred while processing the quotation' };
  }
}; 

    const fetchAllApprovedCustomers = async (attempt = 1) => {
      try {
        const encryptedToken = sessionStorage.getItem('token');
                const token = decryptData(encryptedToken);
                const encryptedOrganizationId = sessionStorage.getItem('organizationId');
                const organizationId = decryptData(encryptedOrganizationId);
        const response = await fetch(fetchurl + `customer/get-all-approved-customers?organizationId=${organizationId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token
          }
        });
        const data = await response.json();
        const output = await handleGetResponse(response, data);
       // console.log(output)
        if (output) {
          // Format data for dropdown
          const formattedCustomers = output.map(customer => ({
            id: customer._id,  // Use customer ID as value
            label: customer.customer_name, // Use customer name as label
            value: customer.customer_name
          }));
    
          setCustomers(formattedCustomers); // Update state with formatted data
         // console.log(formattedCustomers);
// Check for customerId in sessionStorage and set selected customer
const scId = sessionStorage.getItem('customerId');
const storedCustomerId = decryptData(scId);
if (storedCustomerId) {
  console.log(storedCustomerId)
  const selectedCustomer = formattedCustomers.find(customer => customer.id === storedCustomerId);
  if (selectedCustomer) {
    console.log(selectedCustomer)
    setSelectedCustomer(selectedCustomer);
    // Optionally fetch related data for this customer
    //fetchAllprojectsbyCustomer(selectedCustomer.id);
  }
}
} else {
if(response.status === 500) {
  setRetryFunction(() => () => fetchAllApprovedCustomers);
}
}
      }  catch (error) {
        console.error('Failed to fetch:', error);
  
        if (attempt < RETRY_LIMIT) {
          setRetryCount(attempt + 1); // Increment retry count
          setTimeout(() => fetchAllApprovedCustomers(attempt + 1), 1000); // Retry after 1 second
        } else {
          setIsLoading(false);
          setPopupMessage('Internal Server Error. Kindly Retry.');
          setPopupType('500');
          setShowPopup(true);
          setRetryFunction(() => () => fetchAllApprovedCustomers(attempt)); // Set retry function
        }
      }finally {
        setIsLoading(false);
      }
    };

    const fetchAllprojectsbyCustomer = async (id, attempt = 1) => {
      //console.log('Fetching projects for customer_id:', id);
      try {
        const encryptedToken = sessionStorage.getItem('token');
        const token = decryptData(encryptedToken);

        const response = await fetch(`${FetchProjectsUrl}/projects/get-projects-by-customer?customer_id=${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-token': token
          }
        });
        const data = await response.json();
        const output = await handleGetResponse(response, data);
        //console.log(output);
        if (output) {
           const formattedProjects = output.map(project => ({
            id: project?._id,  // Use customer ID as value
            label: project?. projectName, // Use customer name as label
            value: project?.projectName
          }));
    
          setProjects(formattedProjects);

          //console.log(formattedProjects);
        } else {
          if (response.status === 500) {
           // console.log('retry entered');
            setRetryFunction(() => () => fetchAllprojectsbyCustomer(id));
          }
          //console.error(data.message);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
  
        if (attempt < RETRY_LIMIT) {
          setRetryCount(attempt + 1);
          setTimeout(() => fetchAllprojectsbyCustomer(id, attempt + 1), 1000);
        } else {
          setIsLoading(false);
          setPopupMessage('Internal Server Error. Kindly Retry.');
          setPopupType('500');
          setShowPopup(true);
          setRetryFunction(() => () => fetchAllprojectsbyCustomer(id, attempt));
        }
      } finally {
        setIsLoading(false);
      }
    };    

// useEffect(() => {
// if (deliveryAddresses && deliveryAddresses.length > 0 && !selectedDeliveryAddress) {
// const customerId = deliveryAddresses[0].customer_id
// getDefaultDeliveryAddress(customerId)
// }
// }, [deliveryAddresses]);

    const getDefaultDeliveryAddress = async (customer_id) => {
  try {
    if (!customer_id) throw new Error('Customer ID is required');

    const url = process.env.REACT_APP_FORE_BRICKBUCKET;
    const token = sessionStorage.getItem('token');

    const response = await fetch(
      `${url}customer/delivery-address/getDefaultDeliveryAddress?customer_id=${customer_id}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'x-token': token,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.success) {
     setSelectedDeliveryAddress(data.data)
    } else {
      console.error('Failed to fetch default delivery address:', data.message);
      return null;
    }
  } catch (error) {
    console.error('Error fetching default delivery address:', error);
    return null;
  }
};

  const filteredDeliveryAddresses = deliveryAddresses
  .filter(addr => {
    const addrCustomerId =
      typeof addr.customer_id === "object"
        ? addr.customer_id._id
        : addr.customer_id;

    return addrCustomerId === selectedCustomer?.id;
  })
  .map(addr => ({
    value: addr._id,
    addressType: addr.address_type, 
    label: addr.area_sector_locality || "Address not available",
    fullData: addr
  }));


const fetchDeliveryAddresses = async (customerId) => {
  try {
    const encryptedToken = sessionStorage.getItem("token");
    const token = decryptData(encryptedToken);

    if (!customerId || !token) return;

    const response = await fetch(
      `${fetchurl}customer/delivery-address/getDeliveryAddresses?customer_id=${customerId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-token": token,
        },
      }
    );

    const data = await response.json();

    console.log("Delivery addresses for selected customer:", data);

    if (data.success) {
      setDeliveryAddresses(data.data || []);
    } else {
      setDeliveryAddresses([]);
    }

  } catch (error) {
    console.error("Error fetching delivery addresses:", error);
    setDeliveryAddresses([]);
  }
};

//filtered items
  const filteredProducts =
    allProducts.filter((item) =>
      item.product_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase())
    ) || [];

  if (!isOpen) return null;

    const handleCustomerSelect = (option) => {
    setSelectedProject(null); // Reset the selected project
    setSelectedProjectId(null); // Reset the selected project ID
    setSelectedCustomer(option); // Set the selected option object
    console.log(option)
  };

    if (isLoading) {
      return <Loader />;
    }
  
   

  return (
  <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl w-full max-w-7xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
      
      {/* Header */}
      <div className="flex justify-between items-center px-5 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-800">Add Items To Create RFQ</h2>
       <button
  onClick={() => {
    onClose();
    setItems([]);
  }}
  className="p-2 hover:bg-gray-100 rounded-full transition"
>
  <X size={20} className="text-gray-600" />
</button>

      </div>

      {/* Main Content */}
      <div className="flex  overflow-hidden ">
        <div className="w-full flex flex-col bg-white px-8 py-5 overflow-hidden">

          {/* Pricing Type */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-gray-800 mb-2">Select Pricing Type</p>
            <div className="flex gap-3">
              {["project", "general"].map((type) => (
                <div
                  key={type}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border transition-all duration-200 cursor-pointer ${
                    pricingModel === type
                      ? "border-blue-600 bg-blue-50 shadow-sm"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setPricingModel(type)}
                >
                  <div
                    className={`w-3 h-3 rounded-full ${
                      pricingModel === type ? "bg-blue-600" : "bg-gray-300"
                    }`}
                  ></div>
                  <span
                    className={`text-sm font-medium ${
                      pricingModel === type ? "text-blue-700" : "text-gray-700"
                    }`}
                  >
                    {type === "project" ? "Project Pricing" : "Quick Pricing"}
                  </span>
                </div>
              ))}
            </div>
          </div>

        <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
                        <label htmlFor="customername" className="block text-sm font-regular text-[#e54643] w-1/6">
                          Customer Name<span className="text-red-500">*</span>
                        </label>
                        <div className='w-1/3'>
                        <CustomDropdown
                  options={customers}
                  selected={selectedCustomer}
                  onSelect={handleCustomerSelect}
                  placeholder='Select Customer'
                  showAddNew={false}
                  showSearch={true}
                />
                   </div>
        </div>
         <div className="w-full flex flex-row items-center mb-4 mt-5 space-x-2">
                        <label htmlFor="customername" className="block text-sm font-regular text-[#e54643] w-1/6">
                          Delivery Address<span className="text-red-500">*</span>
                        </label>
                        <div className='w-1/3'>
                    <CustomDropdown
  options={filteredDeliveryAddresses}
  selected={
    selectedDeliveryAddress
      ? {
          value: selectedDeliveryAddress._id,
          label: selectedDeliveryAddress.area_sector_locality || "Address not available"
        }
      : null
  }
  onSelect={(option) => {
    setSelectedDeliveryAddress(option.fullData);
  }}
  placeholder="Select Delivery Address"
  showAddNew={false}
  showSearch={true}
/>
                   </div>
        </div>


          {/* Search + Add Item */}
          <div className="flex items-start gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400" size={18} />
              <input
  type="text"
  placeholder="Search or add an item..."
  value={searchQuery}
  onChange={(e) => {
    setSearchQuery(e.target.value);
    setShowSuggestions(true);
  }}
  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 text-sm transition"
/>

              {showSuggestions && searchQuery && (
                <div className="absolute bg-white border border-gray-200 rounded-xl mt-1 w-full shadow-lg z-10 max-h-48 overflow-y-auto">
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((item) => (
                      <div
                        key={item._id}
                        onClick={() => {
                          setSearchQuery(item.product_name);
                          setShowSuggestions(false);
                        }}
                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-700"
                      >
                        {item.product_name}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-2 text-sm text-gray-500">No items found</div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={handleAddItem}
              className="text-sm font-medium py-2 px-4 rounded-md border border-[#f7525a] text-[#f7525a] bg-white hover:bg-[#f7525a] hover:text-white transition-colors duration-200 shadow-sm"
            >
              Add Item
            </button>
          </div>

          {/* Added Items */}     
<div className="flex-1 overflow-y-auto mb-4 space-y-3">
  {items.map((item, index) => (
    <div
      key={item.id}
      className="flex items-center justify-between border border-gray-100 rounded-xl p-3 hover:shadow-sm bg-white transition"
    >
      {/* Left: Image + Name */}
      <div className="flex items-center gap-3">
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="w-10 h-10 rounded-md object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-md bg-gray-200 flex items-center justify-center">
            <FontAwesomeIcon icon={faImage} className="text-gray-400" />
          </div>
        )}
        <span className="font-medium text-gray-800 text-sm">{item.name}</span>
      </div>

      {/* Right: QuantitySelector + Delete Button */}
      <div className="flex items-center gap-2">
        <QuantitySelector
          item={item}
          quantity={item.quantity}
          handleQuantityChange={handleQuantityChange}
          min={1}
          max={100}
          index={index}
        />

        <button
          onClick={() => {
            setItems((prev) => prev.filter((_, i) => i !== index));
          }}
          className="p-2 hover:bg-red-50 rounded-full transition"
          title="Remove item"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  ))}
</div>


          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 border-t pt-4 mt-auto">
            <button
              onClick={() => {
                onClose();
                setItems([]);
              }}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              onClick={createAndSendQuotation}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition"
            >
              Create RFQ
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
);

};


const QuantitySelector = ({ item, quantity, handleQuantityChange, min = 1, max = Infinity, index }) => {
  const [inputValue, setInputValue] = useState(quantity.toString());

  // Sync inputValue with quantity when quantity changes (e.g., from buttons)
  useEffect(() => {
    setInputValue(quantity.toString());
  }, [quantity]);

  // Handle input change (allow any typing, including clearing)
  const handleInputChange = (e) => {
    setInputValue(e.target.value); // Update local state freely
  };

  // Handle blur (validate and update parent)
  const handleInputBlur = () => {
    const numValue = parseInt(inputValue, 10);
    let finalValue;
    if (isNaN(numValue) || numValue < min) {
      finalValue = min; // Clamp to min if invalid or too low
    } else if (numValue > max) {
      finalValue = max; // Clamp to max if too high
    } else {
      finalValue = numValue; // Valid number
    }
    handleQuantityChange(item.id, finalValue, index); // Update parent
    setInputValue(finalValue.toString()); // Sync local state
  };

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => handleQuantityChange(item.id, "dec", index)}
        className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100"
       // disabled={quantity <= min}
      >
        <Minus size={14} />
      </button>
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        className="w-12 text-center text-sm border border-gray-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
        min={min}
        max={max}
      />
      <button
        onClick={() => handleQuantityChange(item.id, "inc", index)}
        className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded-full hover:bg-gray-100"
        disabled={quantity >= max}
      >
        <Plus size={14} />
      </button>
    </div>
  );
};




export default CreateRFQ;



